import { useUserProfileContext } from "@context/user_profile";
import { Navigate } from "react-router";
import EmployerDashboardPage from "@page/employer-dashboard";
import JobSeekerJobMatche from "@page/job-seeker-dashboard/job-matches";
import AgentDashboardPage from "@page/agent-dashboard";
import TrainingDashboardPage from "@page/training-dashboard";
import DriveThruDemoPage from "@page/drive-thru-demo";
import { isOuraEnabled } from "@oura/role";
import { isReceptionistEnabled, isReceptionistRole, RECEPTIONIST_COMPANY_TYPE } from "@receptionist/role";

export default function DashboardPage() {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();

    if (isLoadingUserProfile) return <></>
    if (!userInfo) return <Navigate to="/" />

    // The Receptionist, before the role dispatch below, because a receptionist
    // workspace's owner is an `admin` like any other and would otherwise be
    // handed the recruiter dashboard — a product they did not buy.
    //
    // A receptionist ROLE never renders a dashboard here at all: it grants
    // access to /receptionist and nothing else, and falling through to the
    // bottom of this function returned /unauthorized. That is the bug this
    // fixes — a `receptionist-client` with a perfectly good session, a real
    // merchantId and company_type "receptionist" was told they were not
    // allowed in, by the one screen that had never heard of them.
    //
    // Same shape as the Oura branch further down, including the honesty when
    // the product is off: there is nowhere to send them, and bouncing to
    // /receptionist would loop against that route's own guard.
    if (isReceptionistRole(userInfo.role) || userInfo.company_type === RECEPTIONIST_COMPANY_TYPE) {
        return <Navigate to={isReceptionistEnabled() ? "/receptionist" : "/unauthorized"} />
    }

    if (userInfo.role === "hr" || userInfo.role === "admin" || userInfo.role === "super_admin") {
        return <EmployerDashboardPage />
    } else if (userInfo.role === "job_seeker") {
        return <JobSeekerJobMatche />
    } else if (userInfo.role === "agent") {
        return <AgentDashboardPage />
    } else if (userInfo.role == "training-agent") {
        return <TrainingDashboardPage />
    } else if (userInfo.role === "oura-staff" || userInfo.role === "oura-client") {
        // Oura roles never render a dashboard here — they live entirely under
        // /oura, whose index redirects staff to the pipeline and clients to
        // their own business dashboard.
        //
        // With the product switched off in production there is nowhere to send
        // them: an Oura role grants access to Oura and nothing else, so falling
        // through to a recruiter dashboard would show them someone else's
        // product. /unauthorized is the honest answer, and it terminates —
        // bouncing back to /oura would loop against that route's own guard.
        return <Navigate to={isOuraEnabled() ? "/oura" : "/unauthorized"} />
    } else if (userInfo.company_type === "drive-thru" || userInfo.role === "drive-thru") {
        // Drive-thru demo workspaces (Tealive / Bask Bear) get the POS-screen
        // UI instead of a recruiter dashboard. The BE marks them with BOTH
        // role and company_type == "drive-thru"; company_type is the routing
        // key per the contract. Every /drive-thru endpoint 403s for anyone
        // else, so this is a display route, not the security boundary.
        return <DriveThruDemoPage />
    }

    return <Navigate to="/unauthorized" />
}